import { makeStyles, MenuItem, Theme } from "@material-ui/core";
import { FilterList } from "@material-ui/icons";
import React, { useMemo } from "react";
import { UserType } from "../services/domain/User";
import {
  CCEARoles,
  AoRoles,
  OfqualRoles,
  UserRoleText,
} from "../services/domain/userRole";

import {
  FilterMenu,
  FilterSearch,
} from "@ofqual/portal-components/components/ResponseFilter";

import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { useLookup, useSearch, useUrl } from "@ofqual/portal-core/core/hooks";
import { aoService, teamsService } from "../services";
import { Select } from "@ofqual/portal-components/components/Select";
import { ensureParamIsArray } from "@ofqual/portal-core/core/utils/urlHelper";
import { authSelector } from "../redux/selectors";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { QueryParams } from "@ofqual/portal-core/core/types";

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    menuItem: {
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  };
});

const mapUserRoles = (userType: UserType) => {
  const mappedOfqualRoles = OfqualRoles.map((role) => {
    return { id: role.toString(), label: UserRoleText[role] };
  });
  const mappedCceaRoles = CCEARoles.map((role) => {
    return { id: role.toString(), label: UserRoleText[role] };
  });
  const mappedAoRoles = AoRoles.map((role) => {
    return { id: role.toString(), label: UserRoleText[role] };
  });

  if (userType === UserType.Ofqual) {
    return [
      { id: "Ofqual", label: "Ofqual", disabled: true },
      ...mappedOfqualRoles,
      { id: "CCEA", label: "CCEA Regulation", disabled: true },
      ...mappedCceaRoles,
      { id: "AO", label: "Awarding Organisation", disabled: true },
      ...mappedAoRoles,
    ];
  } else if (userType === UserType.CCEA) {
    return mappedCceaRoles;
  } else {
    return mappedAoRoles;
  }
};

export const UsersFilterControls: React.FC = () => {
  const classes = useStyles();
  const { userType, token } = useAuth(authSelector);

  const { navigateToNextSearch, searchParamsFiltersOnly } = useSearch(useUrl());

  const [teams] = useLookup(
    {
      service: teamsService,
      id: "code",
      label: "name",
    }
  );

  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) => aoService.find(params, { token }),
    }),
    [token]
  );

  return (
    <>
      <FilterSearch
        title="Search..."
        value={searchParamsFiltersOnly.search}
        onChange={(value) =>
          navigateToNextSearch({
            search: value,
          })
        }
        dataTest={"user-search"}
      />
      &nbsp;&nbsp;
      <FilterMenu
        Icon={FilterList}
        title="Filters"
        dataTest="users-filter"
        search={{ navigateToNextSearch, searchParamsFiltersOnly }}
      >
        {(workingParams, dirtyParams, addParam) => (
          <form>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("active") ? classes.root : undefined
                }
                label="Active"
                data-test="users-status-filter"
                value={workingParams.active}
                onChange={(e) => addParam("active", e.target.value)}
              >
                {[
                  { id: "true", label: "Active" },
                  { id: "false", label: "Inactive" },
                  { id: "all", label: "All" },
                ]}
              </Select>
            </MenuItem>

            {userType !== UserType.AO ? (
              <MenuItem className={classes.menuItem}>
                <SearchField
                  searchFieldVariant="multiple"
                  className={
                    dirtyParams.includes("awardingOrgId")
                      ? classes.root
                      : undefined
                  }
                  onChange={(e: any) =>
                    addParam("awardingOrgId", e.target.value)
                  }
                  value={ensureParamIsArray(workingParams.awardingOrgId)}
                  label={"Awarding Organisations"}
                  datatest="users-ao-filter"
                  service={mappedOrgLookupService}
                />
              </MenuItem>
            ) : null}

            {userType === UserType.Ofqual ? (
              <MenuItem className={classes.menuItem}>
                <Select
                  className={
                    dirtyParams.includes("userType") ? classes.root : undefined
                  }
                  label="User Type"
                  data-test="users-type-filter"
                  value={workingParams.userType}
                  onChange={(e) => addParam("userType", e.target.value)}
                >
                  {[UserType.Ofqual, UserType.CCEA, UserType.AO].map(
                    (userType) => ({
                      id: userType.toString(),
                      label:
                        userType === UserType.CCEA
                          ? `${UserType[userType]} Regulation`
                          : UserType[userType],
                    })
                  )}
                </Select>
              </MenuItem>
            ) : null}

            <MenuItem className={classes.menuItem}>
              <Select
                selectVariant="multiple"
                className={
                  dirtyParams.includes("role") ? classes.root : undefined
                }
                label="User Role(s)"
                data-test="users-roles-filter"
                value={ensureParamIsArray(workingParams.role)}
                onChange={(e) => addParam("role", e.target.value)}
              >
                {mapUserRoles(userType as UserType)}
              </Select>
            </MenuItem>

            {userType === UserType.Ofqual ? (
              <MenuItem className={classes.menuItem}>
                <Select
                  selectVariant="multiple"
                  className={
                    dirtyParams.includes("team") ? classes.root : undefined
                  }
                  label="Team(s)"
                  data-test="users-team-filter"
                  value={ensureParamIsArray(workingParams.team)}
                  onChange={(e) => addParam("team", e.target.value)}
                >
                  {teams}
                </Select>
              </MenuItem>
            ) : null}
          </form>
        )}
      </FilterMenu>
    </>
  );
};
